import type {
  PlacementFormState,
  PlacementFormErrors,
  AtsSourceValues,
  Lob,
} from "../types"
import type { SearchableSelectOption } from "../fields/searchable-select"
import { AtsHint } from "../fields/display-helpers"
import { SearchableSelect } from "../fields/searchable-select"
import { FIELD_ANCHOR_ID_PREFIX } from "../constants"
import { useTranslation } from "@/hooks/use-translation"

interface OrgSearchHook {
  search: (term: string) => void
  loading: boolean
  options: SearchableSelectOption[]
}

export interface MappingSectionProps {
  form: PlacementFormState
  errors: PlacementFormErrors
  showError: (field: keyof PlacementFormErrors) => boolean | undefined
  atsSource: AtsSourceValues
  internalCompanySearch: OrgSearchHook
  clientOrgSearch: OrgSearchHook
  changedFields?: {
    internalCompanyChanged?: boolean
    clientOrgChanged?: boolean
    lobChanged?: boolean
  }
  lobs: Lob[]
  onLobCodeChange: (code: string | null) => void
  onInternalCompanyChange: (id: string | null) => void
  onClientOrgChange: (id: string | null) => void
}

export function MappingSection({
  form,
  errors,
  showError,
  atsSource: ats,
  internalCompanySearch,
  clientOrgSearch,
  changedFields,
  lobs,
  onLobCodeChange,
  onInternalCompanyChange,
  onClientOrgChange,
}: MappingSectionProps) {
  const { t } = useTranslation()
  const {
    internalCompanyChanged,
    clientOrgChanged,
    lobChanged,
  } = changedFields ?? {}

  return (
    <section>
      <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wider mb-3">
        {t("sectionMapping")}
      </h3>
      <div className="flex flex-col gap-1">
        {/* Internal Company */}
        <div id={`${FIELD_ANCHOR_ID_PREFIX}internalCompanyId`}>
          <SearchableSelect
            label={t("labelInternalCompany")}
            value={form.internalCompanyId}
            onValueChange={onInternalCompanyChange}
            onSearch={internalCompanySearch.search}
            options={internalCompanySearch.options}
            loading={internalCompanySearch.loading}
            placeholder={t("placeholderSelectInternalCompany")}
            error={
              showError("internalCompanyId")
                ? errors.internalCompanyId
                : undefined
            }
            required
            atsHint={<AtsHint value={ats.internalCompanyName} />}
            changedPill={internalCompanyChanged}
          />
        </div>

        {/* Client Organization */}
        <div id={`${FIELD_ANCHOR_ID_PREFIX}clientOrgId`}>
          <SearchableSelect
            label={t("labelClientOrganization")}
            value={form.clientOrgId}
            onValueChange={onClientOrgChange}
            onSearch={clientOrgSearch.search}
            options={clientOrgSearch.options}
            loading={clientOrgSearch.loading}
            placeholder={t("placeholderSelectClientOrg")}
            error={
              showError("clientOrgId")
                ? errors.clientOrgId
                : undefined
            }
            required
            atsHint={<AtsHint value={ats.clientOrgName} />}
            changedPill={clientOrgChanged}
          />
        </div>

        {/* Line of Business */}
        <div id={`${FIELD_ANCHOR_ID_PREFIX}lobCode`}>
          <SearchableSelect
            label={t("labelLineOfBusiness")}
            value={form.lobCode}
            onValueChange={onLobCodeChange}
            options={lobs.map((l) => ({
              id: l.code,
              name: l.value,
            }))}
            placeholder={t("placeholderSelectLob")}
            error={
              showError("lobCode")
                ? errors.lobCode
                : undefined
            }
            required
            atsHint={
              <AtsHint
                value={
                  ats.lobCode
                    ? lobs.find((l) => l.code === ats.lobCode)?.value ?? ats.lobCode
                    : null
                }
              />
            }
            changedPill={lobChanged}
          />
        </div>
      </div>
    </section>
  )
}
